import React, { useState, useEffect } from 'react';
import styles from './BVLOSOperations.module.css';

const CMS_BASE_URL = process.env.REACT_APP_CMS_BASE_URL;

const BVLOSOperations = ({
  title = "BVLOS Operations",
  operations = [],
  className = ''
}) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isAutoRotating, setIsAutoRotating] = useState(true);

  // Filter operations that have images
  const operationsWithImages = operations.filter(op => op.image);
  const hasImages = operationsWithImages.length > 0;
  const hasMultipleImages = operationsWithImages.length > 1;

  // Auto-rotate images every 3 seconds (only if multiple images)
  useEffect(() => {
    if (!hasMultipleImages || !isAutoRotating) {
      return;
    }

    const interval = setInterval(() => {
      setActiveIndex((prevIndex) => (prevIndex + 1) % operationsWithImages.length);
    }, 3000);

    return () => clearInterval(interval);
  }, [operationsWithImages.length, isAutoRotating, hasMultipleImages]);

  // Handle clicking on an operation item
  const handleOperationClick = (clickedOperation) => {
    if (!hasImages) return;
    
    const imageIndex = operationsWithImages.findIndex(op => op === clickedOperation);
    
    if (imageIndex !== -1) {
      setActiveIndex(imageIndex);
      setIsAutoRotating(false);
      
      // Resume auto-rotation after 6 seconds
      setTimeout(() => {
        setIsAutoRotating(true);
      }, 6000);
    }
  };

  // Handle indicator click
  const handleIndicatorClick = (index) => {
    setActiveIndex(index);
    setIsAutoRotating(false);
    setTimeout(() => setIsAutoRotating(true), 6000);
  };

  const renderOperationIcon = (icon) => {
    if (React.isValidElement(icon)) {
      return (
        <span className={styles.iconWrapper}>
          {icon}
        </span>
      );
    }
    
    if (typeof icon === 'string') {
      return (
        <div className={styles.iconWrapper}>
          <img 
            src={`${CMS_BASE_URL}/attachments/${icon}`}
            alt="Operation icon" 
            className={styles.iconImage}
            loading="lazy"
            width="32"
            height="32"
          />
        </div>
      );
    }
    
    return null;
  };

  // Get current image to display
  const getCurrentImage = () => {
    if (hasImages) {
      return operationsWithImages[activeIndex]?.image;
    }
    return null;
  };

  // Get current image alt text
  const getCurrentImageAlt = () => {
    if (hasImages) {
      return operationsWithImages[activeIndex]?.label || 'BVLOS Operation';
    }
    return 'BVLOS Operation';
  };

  // Check if an operation is currently active
  const isOperationActive = (operation) => {
    if (!hasImages) return false;
    return operationsWithImages[activeIndex] === operation;
  };

  const currentImage = getCurrentImage();

  return (
    <section className={`${styles.bvlosSection} ${className}`}>
      {/* Title Section */}
      <div className={styles.titleContainer}>
        <h3 className={styles.sectionTitle}>{title}</h3>
      </div>

      {/* Content Container */}
      <div className={styles.contentContainer}>
        <div className={styles.bvlosContent}>
          
          {/* Operations List */}
          <div className={styles.operationsContainer}>
            <ul className={styles.operationsList}>
              {operations?.map((op, index) => {
                const isActive = isOperationActive(op);
                const hasImage = Boolean(op.image);
                
                return (
                  <li 
                    key={index} 
                    className={`${styles.operationItem} ${isActive ? styles.operationItemActive : ''} ${hasImage ? styles.operationItemClickable : ''}`}
                    onClick={() => hasImage && handleOperationClick(op)}
                  >
                    <div className={styles.operationContent}>
                      {renderOperationIcon(op.icon)}
                      <p className={styles.operationLabel}>{op.label}</p>
                      
                    </div>
                    { <hr className={styles.operationDivider} />}
                  </li>
                  
                );
              })}
            </ul>
          </div>
          
          {/* Image Display */}
          {hasImages && currentImage && (
            <div className={styles.imageContainer}>
              <div className={styles.imageWrapper}>
                <img 
                  src={`${CMS_BASE_URL}/attachments/${currentImage}`}
                  alt={getCurrentImageAlt()} 
                  className={styles.mainImage}
                  loading="lazy"
                  key={activeIndex}
                  width="450"
                  height="320"
                />
              </div>
              
              {hasMultipleImages && (
                <div className={styles.indicatorsContainer}>
                  {operationsWithImages.map((_, index) => (
                    <button
                      key={index}
                      className={`${styles.indicator} ${index === activeIndex ? styles.indicatorActive : ''}`}
                      onClick={() => handleIndicatorClick(index)}
                      aria-label={`Show image ${index + 1}`}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default BVLOSOperations;